import {
    AttributeSet,
    BundlePricings,
    EntityPlanAttributes,
    PlanAttributeSet,
    PlanMetadata,
    RetailPriceAttributesEnum,
    SetBundlePricingInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';

import { isEmpty } from 'lodash';
import { BundlePricingSubAttribute } from 'src/constants';
import { ROUTES } from 'src/constants/routes';
import { IBreadcrumb } from 'src/types/IBreadcrumb';
import { IBundlePricingRowData } from 'src/types/IBundlePricing';
import { IStepTrackerStep } from 'src/types/IStepTrackerStep';

import { EditBundlePricing } from './EditBundlePricing';
import { ReviewBundlePricing } from './ReviewBundlePricing';

export const ADD_BUNDLE_PRICING_STEPS: IStepTrackerStep[] = [
    {
        stepId: ROUTES.BUNDLE_PRICING.STEP.BUNDLE_PRICING,
        stepTitle: 'Edit bundle pricing',
        StepComponent: EditBundlePricing
    },
    {
        stepId: ROUTES.BUNDLE_PRICING.STEP.REVIEW,
        stepTitle: 'Review and publish',
        StepComponent: ReviewBundlePricing
    }
];

export const ADD_BUNDLE_PRICING_BREADCRUMB_ITEMS: IBreadcrumb[] = [
    {
        link: ROUTES.BUNDLE_PRICING.INDEX,
        title: 'Bundle pricing details'
    },
    {
        link: `${ROUTES.BUNDLE_PRICING.INDEX}${ROUTES.BUNDLE_PRICING.EDIT}${ROUTES.BUNDLE_PRICING.STEP.BUNDLE_PRICING}`,
        title: 'Edit bundle pricing'
    }
];

/**
 * Converts row data to SetBundlePricing API payload
 *
 * @param rowData Bundle pricing row data
 * @param planMetadata PAPER plan metadata to extract names and IDs from
 * @param changedBy User alias
 * @returns SetBundlePricingInput
 */
export const convertBundlePricingRowDataToApiPayload = (
    rowData: IBundlePricingRowData[],
    planMetadata: PlanMetadata,
    changedBy: string
): SetBundlePricingInput => {
    const planNameToIdMap = new Map(
        planMetadata.map((pm) => [pm.paperPlan?.paperPlanName ?? '', pm.paperPlan?.paperPlanId ?? 0])
    );

    // Group row data by plan/territory/start date/end date
    const rowDataGroupedByPlanTerritoryDate = groupRowDataByPlanTerritoryDate(rowData);

    // Build entity plan attributes for each plan/territory/start date/end date
    const attributesGroupedByPlanTerritoryDate = generateEntityPlanAttributesGroupedByPlanTerritoryDate(
        rowDataGroupedByPlanTerritoryDate
    );

    const bundlePricings = generateBundlePricings(attributesGroupedByPlanTerritoryDate, planNameToIdMap);

    return SetBundlePricingInput.clone({ bundlePricings, changedBy });
};

/**
 * Helper function to group row data by plan-territory-startDate-endDate
 *
 * @param rowData Bundle pricing row data
 * @returns Mapping to row data
 */
const groupRowDataByPlanTerritoryDate = (rowData: IBundlePricingRowData[]): Map<string, IBundlePricingRowData[]> => {
    const map = new Map<string, IBundlePricingRowData[]>();

    rowData.forEach((row) => {
        const startDate = Date.parse(row.startDate).toString();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const endDate = isEmpty(row.endDate) ? '-' : Date.parse(row.endDate!).toString();
        const key = [row.planName, row.territory, startDate, endDate].join('#');
        map.set(key, [...(map.get(key) ?? []), row]);
    });

    return map;
};

/**
 * Creates EntityPlanAttributes from a bundle pricing row data mapping
 *
 * @param rowDataMap Mapping of plan-territory-startDate-endDate to row data
 * @returns Mapping of plan-territory-startDate-endDate to EntityPlanAttributes
 */
const generateEntityPlanAttributesGroupedByPlanTerritoryDate = (
    rowDataMap: Map<string, IBundlePricingRowData[]>
): Map<string, EntityPlanAttributes> => {
    const entityPlanAttributesMap = new Map<string, EntityPlanAttributes>();

    rowDataMap.forEach((rows, key) => {
        const entityPlanAttributes: EntityPlanAttributes = [];

        // Currency should be the same for all rows
        entityPlanAttributes.push({
            entityType: {
                entityTypeId: 1
            },
            attribute: RetailPriceAttributesEnum.RETAIL_PRICE_CURRENCY,
            value: rows[0].currency
        });

        rows.forEach((row) => {
            switch (row.planSubAttribute) {
                case BundlePricingSubAttribute.NON_PRIME:
                    entityPlanAttributes.push({
                        entityType: {
                            entityTypeId: 1
                        },
                        attribute: RetailPriceAttributesEnum.STANDALONE_MUSIC_RETAIL_PRICE,
                        value: row.price
                    });
                    break;

                case BundlePricingSubAttribute.PRIME:
                    entityPlanAttributes.push({
                        entityType: {
                            entityTypeId: 1
                        },
                        attribute: RetailPriceAttributesEnum.PRIME_DISCOUNT_STANDALONE_MUSIC_RETAIL_PRICE,
                        value: row.price
                    });
                    break;

                case BundlePricingSubAttribute.IOS:
                    entityPlanAttributes.push({
                        entityType: {
                            entityTypeId: 1
                        },
                        attribute: RetailPriceAttributesEnum.IOS_STANDALONE_MUSIC_RETAIL_PRICE,
                        value: row.price
                    });
                    break;

                case BundlePricingSubAttribute.GOOGLE:
                    entityPlanAttributes.push({
                        entityType: {
                            entityTypeId: 1
                        },
                        attribute: RetailPriceAttributesEnum.GOOGLE_STANDALONE_MUSIC_RETAIL_PRICE,
                        value: row.price
                    });
                    break;

                case BundlePricingSubAttribute.NON_MUSIC:
                    entityPlanAttributes.push({
                        entityType: {
                            entityTypeId: 1
                        },
                        attribute: RetailPriceAttributesEnum.NON_MUSIC_RETAIL_PRICE,
                        value: row.price
                    });
            }
        });

        entityPlanAttributesMap.set(key, entityPlanAttributes);
    });

    return entityPlanAttributesMap;
};

/**
 * Creates BundlePricings from the created EntityPlanAttributes
 *
 * @param entityPlanAttributesMap Mapping of EntityPlanAttributes
 * @param planNameToIdMap PAPER plan name mapping to ID
 * @returns BundlePricings
 */
const generateBundlePricings = (
    entityPlanAttributesMap: Map<string, EntityPlanAttributes>,
    planNameToIdMap: Map<string, number>
): BundlePricings => {
    const attributeSetsByPlan = new Map<number, AttributeSet>();
    entityPlanAttributesMap.forEach((entityPlanAttributes, key) => {
        const [planName, territory, startDateMs, endDateMs] = key.split('#');
        const startDate = new Date(parseInt(startDateMs));
        const endDate = endDateMs === '-' ? undefined : new Date(parseInt(endDateMs));
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const planId = planNameToIdMap.get(planName)!;

        const planAttributeSet: PlanAttributeSet = {
            territory,
            startDate,
            endDate,
            attributes: entityPlanAttributes
        };

        attributeSetsByPlan.set(planId, [...(attributeSetsByPlan.get(planId) ?? []), planAttributeSet]);
    });

    const bundlePricings: BundlePricings = [];
    attributeSetsByPlan.forEach((attributeSet, planId) => {
        bundlePricings.push({
            paperPlanId: planId,
            attributeSets: attributeSet
        });
    });

    return bundlePricings;
};
